/**
 * ============================================================================
 * Factory 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义工厂管理模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createFactoryApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createFactoryApi } from '@bayes-mes/shared/api/factory/factory'
 * export const factoryApi = createFactoryApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await factoryApi.getFactoryList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createFactoryApi } from '@bayes-mes/shared/api/factory/factory'
 * export const factoryApi = createFactoryApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/factory/factory
 * @description Factory 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Factory,                        // 工厂主实体类型
  FactoryQueryParams,              // 列表查询参数
  CreateFactoryForm,               // 创建表单
  UpdateFactoryForm,               // 更新表单
  FactoryStatistics,               // 统计数据
  BatchDeleteFactoryParams,        // 批量删除参数
} from '../../types/factory'

// ===== 导入通用类型 =====
import type { 
  PaginationResult,
  BatchDeleteResult,               // 批量删除结果（通用类型）
} from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Factory 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有工厂相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有工厂相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createFactoryApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取工厂列表（分页）
     * 
     * 使用场景：
     * - 在工厂管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据编码、名称、状态、地区等条件查询数据
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 20，最大 100
     *     示例：per_page: 20（每页20条）
     *   - search?: string - 搜索关键词，搜索工厂编码或名称
     *     示例：search: '深圳'（搜索包含"深圳"的工厂）
     *   - status?: number - 状态筛选：0-停用，1-运营中
     *     示例：status: 1（只查询运营中的工厂）
     *   - country?: string - 国家筛选
     *     示例：country: '中国'
     *   - province?: string - 省份筛选
     *     示例：province: '广东省'
     *   - city?: string - 城市筛选
     *     示例：city: '深圳市'
     *   - only_active?: boolean - 是否只查询启用状态的工厂
     *     示例：only_active: true
     *   - sort_field?: string - 排序字段
     *     示例：sort_field: 'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<PaginationResult<Factory>> 已解包的分页结果
     *   返回的数据结构（后端 paginate() 的 data 字段，适配器已解包）：
     *   {
     *     items: Factory[],        // 工厂列表数组
     *     pagination: {
     *       total: number,         // 总记录数，如 158
     *       perPage: number,       // 每页数量，如 20
     *       currentPage: number,   // 当前页码，如 2
     *       lastPage: number       // 最后一页页码，如 8
     *     }
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在页面上点击"下一页"按钮或输入搜索关键词
     * 2. 前端调用：组件调用 factoryApi.getFactoryList({ page: 2, search: '深圳' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/factories?page=2&search=深圳
     * 5. 后端处理：
     *    - 路由匹配到 FactoryController@index 方法
     *    - 验证查询参数（FactoryQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 FactoryResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<Factory> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await factoryApi.getFactoryList()
     * console.log(page1.data)  // Factory对象数组
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await factoryApi.getFactoryList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await factoryApi.getFactoryList({
     *   search: '深圳'
     * })
     * 
     * // 示例4：组合查询（分页 + 搜索 + 筛选）
     * const result = await factoryApi.getFactoryList({
     *   page: 1,
     *   per_page: 10,
     *   search: '深圳',
     *   status: 1,
     *   province: '广东省',
     *   sort_field: 'created_at',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getFactoryList(params?: FactoryQueryParams) {
      // 调用请求客户端的 get 方法
      // - 第一个参数：URL 路径
      // - 第二个参数：配置对象，{ params } 会被转换为 URL 查询参数
      // - 泛型参数：指定返回数据的类型
      return client.get<PaginationResult<Factory>>(API_ROUTES.factory.list, { params })
    },
    
    /**
     * 获取所有工厂（不分页）
     * 
     * 使用场景：
     * - 在下拉选择器中显示所有工厂选项
     * - 在其他模块需要选择工厂时使用
     * - 导出所有工厂数据
     * 
     * @param params - 查询参数（可选）
     *   - only_active?: boolean - 是否只查询启用状态的工厂
     *     示例：only_active: true（只返回运营中的工厂）
     * 
     * @returns Promise<Factory[]> 工厂列表数组
     * 
     * 数据流向：
     * 1. 用户操作：用户打开工厂选择下拉框
     * 2. 前端调用：factoryApi.getAllFactories({ only_active: true })
     * 3. 发送请求：GET /api/v1/factories/all?only_active=true
     * 4. 后端处理：FactoryController@all 方法
     *    - 查询所有符合条件的工厂（不分页）
     *    - 按 ID 升序排序
     *    - 返回工厂数组
     * 5. 前端显示：在下拉框中展示工厂列表
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有工厂
     * const allFactories = await factoryApi.getAllFactories()
     * 
     * // 只获取运营中的工厂
     * const activeFactories = await factoryApi.getAllFactories({
     *   only_active: true
     * })
     * 
     * // 在表单中使用
     * const factoryOptions = allFactories.map(f => ({
     *   label: f.factoryName,
     *   value: f.id
     * }))
     * ```
     * 
     * 错误处理：
     * - 500：服务器错误
     */
    getAllFactories(params?: { only_active?: boolean }) {
      return client.get<Factory[]>(API_ROUTES.factory.all, { params })
    },
    
    /**
     * 获取工厂详情
     * 
     * 使用场景：
     * - 在工厂详情页面显示完整信息
     * - 编辑工厂时获取当前数据
     * - 查看工厂的详细资料
     * 
     * @param id - 工厂的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的工厂）
     * 
     * @returns Promise<Factory> 单个工厂对象
     *   返回的数据结构：
     *   {
     *     id: number,              // 工厂 ID
     *     factoryCode: string,     // 工厂编码
     *     factoryName: string,     // 工厂名称
     *     country: string | null,  // 国家
     *     province: string | null, // 省份
     *     city: string | null,     // 城市
     *     address: string | null,  // 详细地址
     *     managerName: string | null, // 负责人
     *     phone: string | null,    // 联系电话
     *     email: string | null,    // 邮箱
     *     isDefault: boolean,      // 是否默认工厂
     *     timeZone: string,        // 时区
     *     status: FactoryStatus,   // 状态
     *     createdAt: string,       // 创建时间（ISO 8601格式）
     *     updatedAt: string        // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：factoryApi.getFactoryDetail(123)
     * 3. 发送请求：GET /api/v1/factories/123
     * 4. 后端处理：FactoryController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 通过 FactoryResource 格式化数据
     * 5. 返回数据：单个 Factory 对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的工厂
     * const detail = await factoryApi.getFactoryDetail(123)
     * console.log(detail.factoryName)  // 工厂名称
     * console.log(detail.factoryCode)  // 工厂编码
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const factoryData = await factoryApi.getFactoryDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：工厂不存在
     * - 403：无权限访问该工厂
     * - 422：ID 格式错误（不是数字）
     */
    getFactoryDetail(id: number) {
      // 使用模板字符串拼接 URL，将 ID 添加到路径中
      // 例如：/api/v1/factories/123
      return client.get<Factory>(API_ROUTES.factory.factories(id))
    },
    
    /**
     * 创建新工厂
     * 
     * 使用场景：
     * - 在工厂管理页面点击"新建"按钮
     * - 提交创建工厂的表单
     * - 系统初始化时创建工厂
     * 
     * @param data - 创建工厂的表单数据
     *   字段要求（参考 CreateFactoryForm 类型）：
     *   - factory_code: string - 工厂编码（必填，唯一）
     *   - factory_name: string - 工厂名称（必填）
     *   - country?: string | null - 国家（可选）
     *   - province?: string | null - 省份（可选）
     *   - city?: string | null - 城市（可选）
     *   - address?: string | null - 详细地址（可选）
     *   - manager_name?: string | null - 负责人姓名（可选）
     *   - phone?: string | null - 联系电话（可选）
     *   - email?: string | null - 邮箱（可选，email格式）
     *   - is_default?: boolean | null - 是否默认工厂（可选）
     *   - time_zone?: string | null - 时区（可选）
     *   - status?: number | null - 状态（可选，0或1）
     * 
     * @returns Promise<Factory> 创建成功后的工厂对象（包含生成的 ID）
     *   返回的数据：
     *   {
     *     id: number,          // 自动生成的 ID
     *     factoryCode: string, // 提交的编码
     *     factoryName: string, // 提交的名称
     *     // ... 其他字段
     *     createdAt: string,   // 服务器生成的创建时间
     *     updatedAt: string    // 服务器生成的更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：factoryApi.createFactory({ factory_code: 'F001', factory_name: '深圳工厂' })
     * 4. 发送请求：POST /api/v1/factories
     *    请求体：{ factory_code: 'F001', factory_name: '深圳工厂', ... }（JSON格式）
     * 5. 后端处理：FactoryController@store 方法
     *    - 验证数据（使用 validate() 方法）
     *    - 检查唯一性（编码不能重复）
     *    - 如果设置为默认工厂，取消其他工厂的默认状态
     *    - 插入数据库
     *    - 记录审计日志
     *    - 返回新创建的记录（包含自动生成的 ID 和时间戳）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到列表页或详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新工厂
     * const newData: CreateFactoryForm = {
     *   factory_code: 'F001',
     *   factory_name: '深圳工厂',
     *   country: '中国',
     *   province: '广东省',
     *   city: '深圳市',
     *   address: '南山区科技园',
     *   manager_name: '张三',
     *   phone: '13800138000',
     *   email: 'factory@example.com',
     *   is_default: true,
     *   time_zone: 'Asia/Shanghai',
     *   status: 1
     * }
     * 
     * const created = await factoryApi.createFactory(newData)
     * console.log(created.id)  // 新创建的 ID
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateFactoryForm) => {
     *   try {
     *     const result = await factoryApi.createFactory(formData)
     *     message.success('创建成功')
     *     router.push(`/factory/${result.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失
     *   - 字段格式错误
     *   - 编码重复（违反唯一性约束）
     * - 403：无权限创建
     * - 500：服务器错误（数据库插入失败）
     */
    createFactory(data: CreateFactoryForm) {
      // POST 请求，第二个参数是请求体数据
      // 后端会自动将 snake_case 字段存入数据库
      return client.post<Factory>(API_ROUTES.factory.create, data)  // 注意：factory在organization路由下
    },
    
    /**
     * 更新工厂
     * 
     * 使用场景：
     * - 在工厂编辑页面修改后提交
     * - 更新工厂的部分字段
     * - 修改工厂状态
     * 
     * @param id - 要更新的工厂的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateFactoryForm 类型）：
     *   - 可以只包含部分字段（部分更新）
     *   - 不能修改的字段：id, createdAt（后端会忽略）
     * 
     * @returns Promise<Factory> 更新后的完整工厂对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：factoryApi.updateFactory(123, { factory_name: '新名称' })
     * 3. 发送请求：PUT /api/v1/factories/123
     *    请求体：{ factory_name: '新名称' }（只包含修改的字段）
     * 4. 后端处理：FactoryController@update 方法
     *    - 验证 ID 是否存在
     *    - 验证更新数据（使用 validate() 方法）
     *    - 检查编码唯一性（如果修改了编码）
     *    - 如果设置为默认工厂，取消其他工厂的默认状态
     *    - 更新数据库记录
     *    - 记录审计日志
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新工厂的名称
     * const updated = await factoryApi.updateFactory(123, {
     *   factory_name: '新名称'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await factoryApi.updateFactory(123, {
     *   factory_name: '新名称',
     *   status: 1,
     *   manager_name: '李四'
     * })
     * 
     * // 在编辑表单中使用
     * const handleUpdate = async (id: number, formData: UpdateFactoryForm) => {
     *   try {
     *     const result = await factoryApi.updateFactory(id, formData)
     *     message.success('更新成功')
     *     // 更新本地数据
     *     factoryData.value = result
     *   } catch (error) {
     *     message.error('更新失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：工厂不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     * - 409：冲突（如编码重复）
     */
    updateFactory(id: number, data: UpdateFactoryForm) {
      // PUT 请求，用于完整或部分更新资源
      // 第一个参数：URL（包含 ID）
      // 第二个参数：更新的数据
      return client.put<Factory>(API_ROUTES.factory.factories(id), data)
    },
    
    /**
     * 删除工厂
     * 
     * 使用场景：
     * - 在工厂列表中点击"删除"按钮
     * - 在工厂详情页删除当前记录
     * - 清理测试数据
     * 
     * @param id - 要删除的工厂的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：factoryApi.deleteFactory(123)
     * 4. 发送请求：DELETE /api/v1/factories/123
     * 5. 后端处理：FactoryController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 检查是否可以删除：
     *      - 默认工厂不能删除
     *      - 存在关联车间的工厂不能删除
     *    - 删除记录
     *    - 记录审计日志
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定工厂
     * await factoryApi.deleteFactory(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await factoryApi.deleteFactory(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：工厂不存在（可能已被删除）
     * - 403：无权限删除
     * - 409：无法删除
     *   - 默认工厂不能删除
     *   - 该工厂下有关联的车间，需要先删除车间
     */
    deleteFactory(id: number) {
      // DELETE 请求，用于删除资源
      // 通常不返回数据，或返回简单的成功消息
      return client.delete<void>(API_ROUTES.factory.delete(id))
    },
    
    // ===== 批量操作 =====
    
    /**
     * 批量删除工厂
     * 
     * 使用场景：
     * - 用户在列表中选中多个工厂，点击"批量删除"
     * - 清理多个测试数据
     * 
     * @param data - 批量删除参数
     *   - ids: number[] - 要删除的工厂ID数组
     * 
     * @returns Promise<BatchDeleteResult> 批量删除结果
     *   返回的数据结构：
     *   {
     *     successCount: number,  // 成功删除的数量
     *     failedCount: number,   // 失败的数量
     *     errors: Array<{        // 错误详情
     *       id: number,          // 工厂ID
     *       error: string        // 错误信息
     *     }>
     *   }
     * 
     * 数据流向：
     * 1. 用户在列表中勾选多个工厂
     * 2. 点击批量删除按钮，确认操作
     * 3. 前端调用：factoryApi.batchDeleteFactories({ ids: [1, 2, 3] })
     * 4. 发送请求：POST /api/v1/factories/batch-delete
     *    请求体：{ ids: [1, 2, 3] }
     * 5. 后端处理：FactoryController@batchDestroy 方法
     *    - 验证参数
     *    - 逐个尝试删除
     *    - 记录成功和失败的数量
     *    - 返回结果统计
     * 6. 前端显示：
     *    - 显示操作结果（成功X个，失败X个）
     *    - 如果有失败，显示错误详情
     *    - 刷新列表
     * 
     * 代码示例：
     * ```typescript
     * // 批量删除工厂
     * const result = await factoryApi.batchDeleteFactories({
     *   ids: [1, 2, 3, 4, 5]
     * })
     * 
     * console.log(`成功删除 ${result.successCount} 个`)
     * console.log(`失败 ${result.failedCount} 个`)
     * 
     * if (result.errors.length > 0) {
     *   result.errors.forEach(err => {
     *     console.error(`ID ${err.id}: ${err.error}`)
     *   })
     * }
     * 
     * // 在列表中使用
     * const handleBatchDelete = async (selectedIds: number[]) => {
     *   const confirmed = await showConfirm(`确定要删除选中的 ${selectedIds.length} 个工厂吗？`)
     *   if (!confirmed) return
     *   
     *   try {
     *     const result = await factoryApi.batchDeleteFactories({ ids: selectedIds })
     *     if (result.failedCount === 0) {
     *       message.success('批量删除成功')
     *     } else {
     *       message.warning(`成功删除 ${result.successCount} 个，失败 ${result.failedCount} 个`)
     *     }
     *     await loadList()
     *   } catch (error) {
     *     message.error('批量删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（ids 不是数组）
     * - 403：无权限删除
     * - 部分失败：返回结果中包含失败详情
     */
    batchDeleteFactories(data: BatchDeleteFactoryParams) {
      return client.post<BatchDeleteResult>(API_ROUTES.factory.batchDelete, data)
    },
    
    // ===== 特殊查询 =====
    
    /**
     * 根据工厂编码查询
     * 
     * 使用场景：
     * - 通过扫码枪扫描工厂编码，快速定位工厂
     * - 在其他模块通过编码关联工厂
     * - 验证工厂编码是否存在
     * 
     * @param code - 工厂编码
     * 
     * @returns Promise<Factory> 工厂详情
     * 
     * 数据流向：
     * 1. 用户扫描工厂编码或输入编码
     * 2. 前端调用：factoryApi.getFactoryByCode('F001')
     * 3. 发送请求：GET /api/v1/factories/code/F001
     * 4. 后端处理：FactoryController@findByCode 方法
     *    - 根据编码查询工厂
     *    - 如果不存在，返回 404
     *    - 返回工厂详情
     * 5. 前端显示：展示工厂信息
     * 
     * 代码示例：
     * ```typescript
     * // 根据编码查询
     * const factory = await factoryApi.getFactoryByCode('F001')
     * console.log(factory.factoryName)
     * 
     * // 扫码场景
     * const handleScan = async (code: string) => {
     *   try {
     *     const factory = await factoryApi.getFactoryByCode(code)
     *     // 显示工厂信息或跳转到详情页
     *     router.push(`/factory/${factory.id}`)
     *   } catch (error) {
     *     message.error('未找到该工厂')
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：工厂编码不存在
     */
    getFactoryByCode(code: string) {
      return client.get<Factory>(API_ROUTES.factory.findByCode(code))
    },
    
    /**
     * 获取默认工厂
     * 
     * 使用场景：
     * - 系统初始化时获取默认工厂
     * - 在表单中自动填充默认工厂
     * - 判断是否已设置默认工厂
     * 
     * @returns Promise<Factory> 默认工厂信息
     * 
     * 数据流向：
     * 1. 前端调用：factoryApi.getDefaultFactory()
     * 2. 发送请求：GET /api/v1/factories/default
     * 3. 后端处理：FactoryController@getDefault 方法
     *    - 查询 is_default = true 的工厂
     *    - 如果不存在，返回 400 业务错误
     *    - 返回默认工厂信息
     * 4. 前端使用：在表单中填充或显示
     * 
     * 代码示例：
     * ```typescript
     * // 获取默认工厂
     * const defaultFactory = await factoryApi.getDefaultFactory()
     * console.log(defaultFactory.factoryName)
     * 
     * // 在表单中自动填充
     * const form = ref({
     *   factoryId: null
     * })
     * 
     * onMounted(async () => {
     *   try {
     *     const factory = await factoryApi.getDefaultFactory()
     *     form.value.factoryId = factory.id
     *   } catch (error) {
     *     console.log('未设置默认工厂')
     *   }
     * })
     * ```
     * 
     * 错误处理：
     * - 404：未设置默认工厂
     */
    getDefaultFactory() {
      return client.get<Factory>(API_ROUTES.factory.default)
    },
    
    // ===== 状态管理 =====
    
    /**
     * 设置默认工厂
     * 
     * 使用场景：
     * - 管理员指定某个工厂为默认工厂
     * - 系统初始化时设置默认工厂
     * 
     * @param id - 工厂ID
     * 
     * @returns Promise<Factory> 更新后的工厂信息
     * 
     * 数据流向：
     * 1. 用户在工厂列表或详情页点击"设为默认"
     * 2. 前端调用：factoryApi.setDefaultFactory(123)
     * 3. 发送请求：PUT /api/v1/factories/123/set-default
     * 4. 后端处理：FactoryController@setDefault 方法
     *    - 验证工厂是否存在
     *    - 取消其他工厂的默认状态
     *    - 设置当前工厂为默认
     *    - 返回更新后的工厂信息
     * 5. 前端显示：
     *    - 显示成功提示
     *    - 更新列表显示（标记默认工厂）
     * 
     * 代码示例：
     * ```typescript
     * // 设置默认工厂
     * const updated = await factoryApi.setDefaultFactory(123)
     * console.log(updated.isDefault)  // true
     * 
     * // 在列表中使用
     * const handleSetDefault = async (id: number) => {
     *   try {
     *     await factoryApi.setDefaultFactory(id)
     *     message.success('已设为默认工厂')
     *     await loadList()  // 刷新列表
     *   } catch (error) {
     *     message.error('设置失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：工厂不存在
     * - 403：无权限修改
     * - 500：设置失败
     */
    setDefaultFactory(id: number) {
      return client.put<Factory>(API_ROUTES.factory.setDefault(id))
    },
    
    // ===== 统计相关 =====
    
    /**
     * 获取工厂统计信息
     * 
     * 使用场景：
     * - 在工厂详情页显示统计数据
     * - 在仪表盘展示工厂概况
     * - 分析工厂资源使用情况
     * 
     * @param id - 工厂ID
     * 
     * @returns Promise<FactoryStatistics> 统计信息
     *   返回的数据结构：
     *   {
     *     factoryId: number,              // 工厂ID
     *     factoryCode: string,            // 工厂编码
     *     factoryName: string,            // 工厂名称
     *     workshopCount: number,          // 车间总数
     *     productionLineCount: number,    // 生产线总数
     *     workstationCount: number,       // 工位总数
     *     activeWorkshopCount: number,    // 启用中的车间数量
     *     activeLineCount: number,        // 启用中的生产线数量
     *     activeWorkstationCount: number  // 启用中的工位数量
     *   }
     * 
     * 数据流向：
     * 1. 用户打开工厂详情页或仪表盘
     * 2. 前端调用：factoryApi.getFactoryStatistics(123)
     * 3. 发送请求：GET /api/v1/factories/123/statistics
     * 4. 后端处理：FactoryController@statistics 方法
     *    - 查询工厂信息
     *    - 统计车间、生产线、工位数量
     *    - 统计启用状态的数量
     *    - 返回统计数据
     * 5. 前端显示：
     *    - 在卡片中展示统计数据
     *    - 生成图表
     * 
     * 代码示例：
     * ```typescript
     * // 获取统计信息
     * const stats = await factoryApi.getFactoryStatistics(123)
     * console.log(`车间总数：${stats.workshopCount}`)
     * console.log(`生产线总数：${stats.productionLineCount}`)
     * console.log(`工位总数：${stats.workstationCount}`)
     * 
     * // 在仪表盘中使用
     * const factoryStats = ref<FactoryStatistics | null>(null)
     * 
     * onMounted(async () => {
     *   const { id } = useRoute().params
     *   factoryStats.value = await factoryApi.getFactoryStatistics(Number(id))
     * })
     * 
     * // 显示利用率
     * const workshopUtilization = computed(() => {
     *   if (!factoryStats.value) return 0
     *   const total = factoryStats.value.workshopCount
     *   const active = factoryStats.value.activeWorkshopCount
     *   return total > 0 ? (active / total * 100).toFixed(2) : 0
     * })
     * ```
     * 
     * 错误处理：
     * - 404：工厂不存在
     * - 500：统计查询失败
     */
    getFactoryStatistics(id: number) {
      return client.get<FactoryStatistics>(API_ROUTES.factory.statistics(id))
    },
  }
}

